/**
 * @fileoverview Connect governed document templates to collection browsing, preview, and explicit administration. Shared metadata subscriptions supply server state; selected versions anchor template actions. `kc-internal-backend/functions/document_templates.py` owns template records and managed archive writes own source versions. Uploading, publishing, or adopting a template remain explicit actions rather than effects of opening its preview.
 */

import useDocumentData from '../../store/Documents/useDocumentData';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import AppIcon from '../shared/AppIcon';
import './TemplateWorkspace.css';

const EMPTY_IMPORT = {
  title: '',
  template_key: '',
  template_type: 'engagement_letter',
  description: '',
};



function statusPresentation(template) {
  if (template.status === 'retired') return { icon: 'pi pi-ban', label: 'Retired', tone: 'retired' };
  if (!template.published_revision_id) return { icon: 'pi pi-pencil', label: 'Draft', tone: 'draft' };
  if (template.has_unpublished_changes) return { icon: 'pi pi-pencil', label: 'Draft changes', tone: 'draft' };
  return { icon: 'pi pi-check-circle', label: 'Published', tone: 'published' };
}



function validationPresentation(template) {
  const validation = template.validation || {};
  if (validation.status === 'passed' && validation.revision_id === template.current_revision_id) {
    return { icon: 'pi pi-shield', label: 'Validated', tone: 'passed' };
  }
  if (validation.status === 'failed' && validation.revision_id === template.current_revision_id) {
    return { icon: 'pi pi-exclamation-triangle', label: 'Needs attention', tone: 'failed' };
  }
  return { icon: 'pi pi-shield', label: 'Not validated', tone: 'pending' };
}



function typeLabel(value) {
  if (value === 'engagement_letter') return 'Engagement letter';
  return 'Document';
}



function keyFromTitle(title, templateType) {
  const suffix = String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const prefix = templateType === 'engagement_letter' ? 'engagement_letter' : 'document';
  return suffix ? `${prefix}.${suffix}` : `${prefix}.`;
}



function TemplateWorkspace({ onOpenDocument }) {
  const dataCache = useDocumentData();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [action, setAction] = useState('');
  const [actionFeedback, setActionFeedback] = useState(null);
  const [menuTemplateId, setMenuTemplateId] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importForm, setImportForm] = useState(EMPTY_IMPORT);
  const [importFile, setImportFile] = useState(null);
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);

  const loadTemplates = useCallback(

  ({ force = true } = {}) => {
    const url = '/simul/archive/templates?scope=workspace';
    setLoading(!dataCache.read(url));
    setError('');
    return dataCache.load('templates', url, (data) => {
      setTemplates(data.templates || []);
      setLoading(false);
    }, { force, onClear: () => setTemplates([]), onError: (error) => {
      setLoading(false);
      setError(error.response?.data?.error || error.message || 'Templates could not be loaded.');
    } }).catch(() => null);
  }, [dataCache]);

  useEffect(() => { loadTemplates({ force: false }); }, [loadTemplates]);

  useEffect(

  () => {
    if (!menuTemplateId) return undefined;
    const closeMenu =

    (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'click' && event.target.closest('.documents-template-card__menu')) return;
      setMenuTemplateId('');
    };
    document.addEventListener('click', closeMenu);
    document.addEventListener('keydown', closeMenu);
    return () => {
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('keydown', closeMenu);
    };
  }, [menuTemplateId]);

  const counts = useMemo(() => ({
    published: templates.filter((template) => template.status === 'published' && !template.has_unpublished_changes).length,
    drafts: templates.filter((template) => template.status !== 'retired' && (template.status !== 'published' || template.has_unpublished_changes)).length,
  }), [templates]);

  const updateTemplate =

  (nextTemplate) => {
    if (!nextTemplate?.template_id) return;
    setTemplates((current) => current.map((template) => (
      template.template_id === nextTemplate.template_id ? nextTemplate : template
    )));
  };

  const runAction =

  async (template, name) => {
    const actionKey = `${name}:${template.template_id}`;
    if (action) return;
    setAction(actionKey);
    setError('');
    setActionFeedback(null);
    try {
      const response = await api.post(
        `/simul/archive/templates/${encodeURIComponent(template.template_id)}/${name}`,
        template.current_revision_id ? { revision_id: template.current_revision_id } : {},
      );
      const nextTemplate = response.data?.template;
      updateTemplate(nextTemplate);
      if (name === 'validate') {
        const validation = response.data?.validation || nextTemplate?.validation || {};
        const scenarios = Number(validation.scenario_count || 0);
        const warnings = Array.isArray(validation.warnings) ? validation.warnings.length : 0;
        const errors = Array.isArray(validation.errors) ? validation.errors.length : 0;
        setActionFeedback({
          templateId: template.template_id,
          tone: validation.status === 'passed' ? 'success' : 'error',
          message: validation.status === 'passed'
            ? `Template test passed${scenarios ? ` all ${scenarios} scenarios` : ''}${warnings ? ` with ${warnings} warning${warnings === 1 ? '' : 's'}` : ''}.`
            : `Template test found ${errors || 'one or more'} issue${errors === 1 ? '' : 's'}.`,
        });
      } else if (name === 'publish') {
        setActionFeedback({ templateId: template.template_id, tone: 'success', message: 'This revision is now published.' });
      } else if (name === 'retire') {
        setActionFeedback({ templateId: template.template_id, tone: 'neutral', message: 'This template has been retired.' });
      }
    } catch (requestError) {
      const detail = requestError?.response?.data?.error || requestError.message || `Template ${name} failed.`;
      setError(detail);
      setActionFeedback({ templateId: template.template_id, tone: 'error', message: detail });
      if (requestError?.response?.data?.template) updateTemplate(requestError.response.data.template);
    } finally {
      setAction('');
    }
  };

  const retire =

  (template) => {
    setMenuTemplateId('');
    if (!window.confirm(`Retire ${template.title}? New documents and automations will stop using it.`)) return;
    runAction(template, 'retire');
  };

  const openImport =

  () => {
    setImportForm(EMPTY_IMPORT);
    setImportFile(null);
    setImportError('');
    setImportOpen(true);
  };

  const updateImport =

  (updates) => {
    setImportForm(

    (current) => {
      const next = { ...current, ...updates };
      if ('title' in updates && (!current.template_key || current.template_key === keyFromTitle(current.title, current.template_type))) {
        next.template_key = keyFromTitle(next.title, next.template_type);
      }
      if ('template_type' in updates && (!current.template_key || current.template_key === keyFromTitle(current.title, current.template_type))) {
        next.template_key = keyFromTitle(next.title, next.template_type);
      }
      return next;
    });
  };

  const submitImport =

  async () => {
    if (!importFile || !importForm.title.trim() || !importForm.template_key.trim() || importing) return;
    setImporting(true);
    setImportError('');
    const formData = new FormData();
    formData.append('file', importFile);
    Object.entries(importForm).forEach(([key, value]) => formData.append(key, value));
    formData.append('engine', importForm.template_type === 'engagement_letter' ? 'docxtpl' : 'docx');
    try {
      const response = await api.post('/simul/archive/templates', formData);
      const created = response.data?.template;
      if (created) setTemplates((current) => [...current, created].sort((left, right) => left.title.localeCompare(right.title)));
      setImportOpen(false);
    } catch (requestError) {
      setImportError(requestError?.response?.data?.error || requestError.message || 'Template could not be imported.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="documents-template-workspace" aria-label="Document templates">
      <header className="documents-template-workspace__header">
        <span className="documents-template-workspace__title">
          <span className="documents-template-workspace__icon" aria-hidden="true"><AppIcon icon="pi pi-clone" /></span>
          <span>
            <strong>Templates</strong>
            <small>{counts.published} published · {counts.drafts} in draft</small>
          </span>
        </span>
        <span className="documents-template-workspace__actions">
          <button type="button" onClick={loadTemplates} disabled={loading} aria-label="Refresh templates" title="Refresh templates">
            <AppIcon icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} aria-hidden="true" />
          </button>
          <button type="button" className="is-primary" onClick={openImport}>
            <AppIcon icon="pi pi-plus" aria-hidden="true" />
            Import template
          </button>
        </span>
      </header>

      {error && <div className="documents-archive-error" role="alert">{error}</div>}
      {loading && templates.length === 0 ? (
        <div className="documents-archive-empty">Loading templates…</div>
      ) : templates.length === 0 ? (
        <div className="documents-template-workspace__empty">
          <AppIcon icon="pi pi-file-word" aria-hidden="true" />
          <strong>No managed templates yet</strong>
          <button type="button" onClick={openImport}>Import the first template</button>
        </div>
      ) : (
        <div className="documents-template-grid">
          {templates.map(

          (template) => {
            const status = statusPresentation(template);
            const validation = validationPresentation(template);
            const validationCurrent = template.validation?.status === 'passed'
              && template.validation?.revision_id === template.current_revision_id;
            const busy = action.endsWith(`:${template.template_id}`);
            return (
              <article className="documents-template-card" key={template.template_id}>
                <header>
                  <span className="documents-template-card__file" aria-hidden="true"><AppIcon icon="pi pi-file-word" /></span>
                  <span className="documents-template-card__identity">
                    <strong>{template.title}</strong>
                    <code>{template.template_key || 'Legacy template'}</code>
                  </span>
                  <span className={`documents-template-badge is-${status.tone}`}>
                    <AppIcon icon={status.icon} aria-hidden="true" />{status.label}
                  </span>
                </header>

                <div className="documents-template-card__signals">
                  <span><AppIcon icon="pi pi-bolt" aria-hidden="true" />{typeLabel(template.template_type)}</span>
                  <span className={`is-${validation.tone}`}><AppIcon icon={validation.icon} aria-hidden="true" />{validation.label}</span>
                  {template.used_by?.length > 0 && <span><AppIcon icon="pi pi-cog" aria-hidden="true" />Engagement automation</span>}
                </div>

                {template.validation?.status === 'failed' && template.validation?.errors?.length > 0 && (
                  <div className="documents-template-card__validation" role="status">
                    {template.validation.errors[0]}
                  </div>
                )}

                {actionFeedback?.templateId === template.template_id && (
                  <div
                    className={`documents-template-card__feedback is-${actionFeedback.tone}`}
                    role={actionFeedback.tone === 'error' ? 'alert' : 'status'}>
                    <AppIcon icon={actionFeedback.tone === 'error' ? 'pi pi-exclamation-triangle' : actionFeedback.tone === 'success' ? 'pi pi-check-circle' : 'pi pi-info-circle'} aria-hidden="true" />
                    <span>{actionFeedback.message}</span>
                  </div>
                )}

                <footer>
                  <button
                    type="button"
                    className="documents-template-card__open"
                    disabled={!template.document_id}
                    onClick={() => onOpenDocument(template.document_id)}>
                    Open workspace<AppIcon icon="pi pi-arrow-right" aria-hidden="true" />
                  </button>
                  {!template.legacy && template.status !== 'retired' && <>
                    <button type="button" disabled={busy} onClick={() => runAction(template, 'validate')}>
                      <AppIcon icon={action === `validate:${template.template_id}` ? 'pi pi-spin pi-spinner' : 'pi pi-shield'} aria-hidden="true" />
                      Validate
                    </button>
                    <button
                      type="button"
                      className="is-publish"
                      disabled={busy || !validationCurrent || (!template.has_unpublished_changes && template.status === 'published')}
                      onClick={() => runAction(template, 'publish')}>
                      <AppIcon icon={action === `publish:${template.template_id}` ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" />
                      Publish
                    </button>
                  </>}
                  {template.status === 'published' && (
                    <span className="documents-template-card__menu">
                      <button
                        type="button"
                        className="is-quiet"
                        disabled={busy}
                        aria-label={`More actions for ${template.title}`}
                        aria-haspopup="menu"
                        aria-expanded={menuTemplateId === template.template_id}
                        onClick={() => setMenuTemplateId((current) => current === template.template_id ? '' : template.template_id)}>
                        <AppIcon icon="pi pi-ellipsis-h" aria-hidden="true" />
                      </button>
                      {menuTemplateId === template.template_id && (
                        <span className="documents-template-card__menu-popover" role="menu" aria-label={`Actions for ${template.title}`}>
                          <button type="button" role="menuitem" onClick={() => retire(template)}>
                            <AppIcon icon="pi pi-ban" aria-hidden="true" />
                            Retire template
                          </button>
                        </span>
                      )}
                    </span>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      )}

      {importOpen && (
        <div className="documents-vcs-modal-backdrop" role="presentation" onClick={(event) => {
          if (event.target === event.currentTarget && !importing) setImportOpen(false);
        }}>
          <div className="documents-vcs-modal documents-template-import" role="dialog" aria-modal="true" aria-labelledby="documents-template-import-title">
            <header>
              <h2 id="documents-template-import-title">Import template</h2>
              <button type="button" aria-label="Close template import" onClick={() => !importing && setImportOpen(false)}><AppIcon icon="pi pi-times" /></button>
            </header>
            <label>
              <span>DOCX file</span>
              <input type="file" accept=".docx" onChange={(event) => setImportFile(event.target.files?.[0] || null)} />
            </label>
            <label>
              <span>Name</span>
              <input value={importForm.title} onChange={(event) => updateImport({ title: event.target.value })} placeholder="Standard engagement letter" />
            </label>
            <label>
              <span>Type</span>
              <select value={importForm.template_type} onChange={(event) => updateImport({ template_type: event.target.value })}>
                <option value="engagement_letter">Engagement letter</option>
                <option value="document">Document starting point</option>
              </select>
            </label>
            <label>
              <span>Stable key</span>
              <input value={importForm.template_key} onChange={(event) => updateImport({ template_key: event.target.value })} placeholder="engagement_letter.standard" />
            </label>
            <label>
              <span>Description</span>
              <textarea rows={3} value={importForm.description} onChange={(event) => updateImport({ description: event.target.value })} />
            </label>
            {importError && <div className="documents-archive-error" role="alert">{importError}</div>}
            <footer>
              <button type="button" className="documents-archive-link-button" onClick={() => !importing && setImportOpen(false)}>Cancel</button>
              <button
                type="button"
                className="documents-workspace-action-button is-primary"
                disabled={importing || !importFile || !importForm.title.trim() || !importForm.template_key.trim()}
                onClick={submitImport}>
                <AppIcon icon={importing ? 'pi pi-spin pi-spinner' : 'pi pi-upload'} aria-hidden="true" />
                {importing ? 'Importing…' : 'Import draft'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
}

export default TemplateWorkspace;
